import { useCallback, useEffect, useMemo, useState } from 'react';
import { config } from '../../shared/config';
import { handleTokenExpiration } from '../../shared/authUtils';

const API_BASE_URL = config.apiBaseUrl;

// Import the getIdToken function from mockData
async function getIdToken(): Promise<string> {
  const token = await OfficeRuntime.storage.getItem("token");
  return token;
}

export interface AdvertisingRaw {
  columns: string[];
  data: any[]; 
}

export interface UseAdvertisingDataReturn {
  raw: AdvertisingRaw | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Normalize various API shapes to { columns, data }
function normalizeToColumnsData(payload: any): AdvertisingRaw {
  if (!payload) return { columns: [], data: [] };

  // If already in { columns, data } shape
  if (Array.isArray(payload?.columns) && Array.isArray(payload?.data)) {
    return { columns: payload.columns, data: payload.data };
  }

  // If array of objects
  if (Array.isArray(payload)) {
    const first = payload[0] || {};
    const columns = Object.keys(first);
    const data = payload.map((row: any) => columns.map(c => row?.[c] ?? null));
    return { columns, data };
  }

  // If object with `results` or `items`
  const rows = payload?.results || payload?.items || [];
  if (Array.isArray(rows)) {
    const first = rows[0] || {};
    const columns = Object.keys(first);
    const data = rows.map((row: any) => columns.map(c => row?.[c] ?? null));
    return { columns, data };
  }

  return { columns: [], data: [] };
}

export function useAdvertisingData(
  limit: number = 50000,
  startDate?: Date | null,
  endDate?: Date | null
): UseAdvertisingDataReturn {
  const [raw, setRaw] = useState<AdvertisingRaw | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get authentication token
      const token = await getIdToken();
      if (!token) {
        throw new Error('Token is not configured. Please login again.');
      }

      // Build endpoint and include Authorization header
      const adsUrl = `${API_BASE_URL}/amazon-ads-data`;
      const base = config?.endpoints?.marketingData || adsUrl;

      // If both dates are provided, use the DISTINCT endpoint to fetch unique campaign/keyword pairs
      let url: string;
      if (startDate && endDate) {
        const start = startDate.toISOString().slice(0, 10);
        const end = endDate.toISOString().slice(0, 10);
        url = `${API_BASE_URL}/amazon-ads-data/distinct?start_date=${encodeURIComponent(
          start
        )}&end_date=${encodeURIComponent(end)}`;
      } else {
        url = `${base}?limit=${encodeURIComponent(String(limit))}`;
      }
      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json, text/plain',
        },
      });
      
      if (resp.status === 401) {
        await handleTokenExpiration('Token expired during advertising data fetch');
        throw new Error('Token is not configured. Please login again.');
      }
      
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const normalized = normalizeToColumnsData(json);
      setRaw(normalized);
    } catch (e: any) {
      setError(e?.message || 'Failed to load advertising data');
      setRaw({ columns: [], data: [] });
    } finally {
      setIsLoading(false);
    }
  }, [limit, startDate?.toISOString(), endDate?.toISOString()]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return useMemo(() => ({ raw, isLoading, error, refresh }), [raw, isLoading, error, refresh]);
}


